<template>
  <!--
    Move orders from one unreleased batch to another of the same store.

    The panel owns the call and the reload; this dialog only collects WHICH
    orders and WHERE, reads the consequence back (counts on both sides, and
    that nothing is charged by moving), and emits `confirm`. The server
    re-checks every rule (services/batch_order_move.py), so the choices here
    are a convenience, not the guard.
  -->
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="mv-card" data-testid="move-dialog" role="dialog" :aria-label="`Move orders out of batch ${source.id}`">
      <header class="mv-head">
        <h2 class="mv-title">Move orders out of batch {{ source.id }}</h2>
      </header>

      <div class="mv-body">
        <p class="mv-nocharge" data-testid="move-no-charge">
          <i class="material-icons mv-nocharge__icon" aria-hidden="true">verified_user</i>
          <span>
            Moving charges nothing. Each card is charged when the batch the order
            is in is released.
          </span>
        </p>

        <section class="mv-section">
          <div class="mv-section__head">
            <span class="mv-label">Orders</span>
            <label class="mv-check mv-check--all">
              <input
                type="checkbox"
                data-testid="move-pick-all"
                :checked="allPicked"
                :disabled="busy"
                @change="setAll($event.target.checked)"
              />
              <span>All {{ pluralOrders(movable.length) }}</span>
            </label>
          </div>
          <ul class="mv-list">
            <li v-for="o in movable" :key="o.id">
              <label class="mv-check">
                <input
                  type="checkbox"
                  :data-testid="`move-pick-${o.id}`"
                  :checked="pickedIds.includes(o.id)"
                  :disabled="busy"
                  @change="setPicked(o.id, $event.target.checked)"
                />
                <span class="mv-order">
                  <span class="mv-order__who">{{ o.shopper || "Order" }}</span>
                  <span class="mv-order__num">{{ o.orderNumber || o.id }}</span>
                </span>
              </label>
            </li>
          </ul>
        </section>

        <section class="mv-section">
          <span class="mv-label">Move to</span>
          <div class="mv-dests">
            <button
              v-for="d in destinations"
              :key="d.id"
              type="button"
              class="mv-dest"
              :class="{ 'mv-dest--on': destinationId === d.id }"
              :data-testid="`move-dest-${d.id}`"
              :aria-pressed="destinationId === d.id ? 'true' : 'false'"
              :disabled="busy"
              @click="destinationId = d.id"
            >
              <span class="mv-dest__id">{{ d.id }}</span>
              <span class="mv-dest__meta">
                {{ statusLabel(d.status) }} &middot; {{ pluralOrders((d.orders || []).length) }}
              </span>
            </button>
          </div>
        </section>

        <p v-if="destination" class="mv-summary" data-testid="move-summary">
          Move {{ pluralOrders(pickedIds.length) }} from {{ source.id }} to {{ destination.id }}.
          {{ source.id }} will have {{ pluralOrders(sourceAfter) }};
          {{ destination.id }} will have {{ pluralOrders(destinationAfter) }}.
          The moved orders follow {{ destination.id }}'s release time.
          <template v-if="destination.status === 'open'">
            The moved orders count toward {{ destination.id }}'s release threshold.
          </template>
        </p>

        <p v-if="error" class="mv-error" data-testid="move-error" role="alert">{{ error }}</p>
      </div>

      <footer class="mv-foot">
        <button
          type="button"
          class="bm-btn"
          data-testid="move-cancel"
          :disabled="busy"
          @click="$emit('update:modelValue', false)"
        >
          Not now
        </button>
        <button
          type="button"
          class="bm-btn bm-btn--primary"
          data-testid="move-confirm"
          :disabled="!canConfirm"
          @click="confirm"
        >
          {{ busy ? "Moving..." : confirmLabel }}
        </button>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import { statusMeta } from "./batchStatus";

export default {
  name: "MoveOrdersDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // The batch the orders are moving OUT of.
    source: { type: Object, required: true },
    // The order the Move control was pressed on; preselected.
    initialOrder: { type: Object, default: null },
    // The store's OTHER editable batches, decided by the panel.
    destinations: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: ["update:modelValue", "confirm"],
  data() {
    return {
      pickedIds: this.initialOrder ? [this.initialOrder.id] : [],
      destinationId: null,
    };
  },
  computed: {
    // A held-back order is card recovery's to move, and the server refuses
    // it; it is not offered here either.
    movable() {
      return (this.source.orders || []).filter((o) => !o.held);
    },
    allPicked() {
      return this.movable.length > 0 && this.movable.every((o) => this.pickedIds.includes(o.id));
    },
    destination() {
      return this.destinations.find((d) => d.id === this.destinationId) || null;
    },
    sourceAfter() {
      return (this.source.orders || []).length - this.pickedIds.length;
    },
    destinationAfter() {
      return ((this.destination && this.destination.orders) || []).length + this.pickedIds.length;
    },
    canConfirm() {
      return !this.busy && Boolean(this.destination) && this.pickedIds.length > 0;
    },
    confirmLabel() {
      if (!this.destination) return "Move";
      return `Move ${this.pluralOrders(this.pickedIds.length)} to ${this.destination.id}`;
    },
  },
  methods: {
    pluralOrders(n) {
      return `${n} order${n === 1 ? "" : "s"}`;
    },
    statusLabel(status) {
      return statusMeta(status).label;
    },
    setPicked(id, on) {
      const rest = this.pickedIds.filter((x) => x !== id);
      this.pickedIds = on ? [...rest, id] : rest;
    },
    setAll(on) {
      this.pickedIds = on ? this.movable.map((o) => o.id) : [];
    },
    confirm() {
      if (!this.canConfirm) return;
      // Emitted in the source batch's order, so the request is stable.
      const orders = this.movable.filter((o) => this.pickedIds.includes(o.id));
      this.$emit("confirm", { orders, destination: this.destination });
    },
  },
};
</script>

<style scoped>
.mv-card {
  background: var(--tf-surface);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.mv-head {
  padding: 16px 20px;
  border-bottom: 1px solid var(--tf-border);
}

.mv-title {
  font-size: 17px;
  font-weight: 500;
  margin: 0;
}

.mv-body {
  padding: 16px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 13.5px;
  color: var(--tf-fg-2);
}

.mv-nocharge {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--tf-success-soft);
  border: 1px solid var(--tf-chip-success-border);
  color: var(--tf-success-dark);
  line-height: 1.5;
}

.mv-nocharge__icon {
  font-size: 18px;
}

.mv-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mv-section__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mv-label {
  font-weight: 500;
  color: var(--tf-fg-1);
}

.mv-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid var(--tf-border);
  border-radius: 8px;
}

.mv-list li + li {
  border-top: 1px solid var(--tf-border);
}

.mv-check {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 12px;
  cursor: pointer;
}

.mv-check--all {
  padding: 0;
}

.mv-order {
  display: flex;
  justify-content: space-between;
  flex: 1;
  gap: 8px;
}

.mv-order__num {
  color: var(--tf-fg-3);
}

.mv-dests {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mv-dest {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid var(--tf-border-strong);
  border-radius: 8px;
  background: var(--tf-surface);
  font-family: inherit;
  cursor: pointer;
}

.mv-dest--on {
  border-color: var(--tf-primary);
  box-shadow: 0 0 0 1px var(--tf-primary);
}

.mv-dest__id {
  font-weight: 500;
}

.mv-dest__meta {
  font-size: 12px;
}

.mv-summary {
  margin: 0;
  line-height: 1.6;
}

.mv-error {
  margin: 0;
  color: var(--tf-error);
}

.mv-foot {
  padding: 12px 20px 18px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid var(--tf-border);
}

.bm-btn {
  border-radius: var(--tf-radius-pill, 9999px);
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
